import { useEffect, useState } from 'react';
import { getMuscleLabel } from './exercises';
import {
  appMuscleKeysForGuide,
  findGuideExercise,
  getGuideFrames,
  googleSearchUrl,
} from './exerciseGuide';

const FRAME_ORDER = [0, 1, 2, 1];
const FRAME_MS = 420;

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return reduced;
}

function usePingPong(frameCount, enabled) {
  const [step, setStep] = useState(0);
  const reduced = useReducedMotion();
  useEffect(() => {
    if (!enabled || reduced || frameCount < 2) return undefined;
    const id = window.setInterval(() => {
      setStep((current) => (current + 1) % FRAME_ORDER.length);
    }, FRAME_MS);
    return () => window.clearInterval(id);
  }, [enabled, reduced, frameCount]);
  if (!enabled || reduced || frameCount < 2) return 0;
  return FRAME_ORDER[step % FRAME_ORDER.length];
}

function GuideLoop({ frames, alt, activeIndex, onError, className }) {
  if (!frames?.length) return null;
  return (
    <span className={`guide-loop${className ? ` ${className}` : ''}`}>
      {frames.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={i === activeIndex ? alt : ''}
          className={i === activeIndex ? 'is-active' : ''}
          onError={i === 0 ? onError : undefined}
        />
      ))}
    </span>
  );
}

export function GuideThumb({ name, onOpen, size = 44 }) {
  const [hidden, setHidden] = useState(false);
  const frames = getGuideFrames(name);
  const activeIndex = usePingPong(frames?.length || 0, Boolean(frames) && !hidden);
  if (!frames || hidden) return null;
  return (
    <button
      type="button"
      className="guide-thumb"
      style={{ width: size, height: size }}
      onClick={(e) => {
        e.stopPropagation();
        onOpen?.(name);
      }}
      aria-label={name}
    >
      <GuideLoop
        frames={frames}
        alt=""
        activeIndex={activeIndex}
        onError={() => setHidden(true)}
      />
    </button>
  );
}

export function GoogleSearchLink({ name, language, children, className }) {
  if (!name?.trim()) return null;
  return (
    <a
      className={className || 'btn btn-dark'}
      href={googleSearchUrl(name, language)}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
}

export function GuideSheet({ name, t, language, onClose }) {
  const exercise = name ? findGuideExercise(name) : null;
  const frames = exercise ? getGuideFrames(exercise) : null;
  const muscles = appMuscleKeysForGuide(exercise);
  const activeIndex = usePingPong(frames?.length || 0, Boolean(frames));

  useEffect(() => {
    if (!exercise) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [exercise, onClose]);

  if (!exercise || !frames) return null;

  const labels = [t.guideStart, t.guideMid, t.guideEnd];

  return (
    <div className="guide-sheet-backdrop" onClick={onClose} role="presentation">
      <div
        className="guide-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={exercise.name}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="guide-sheet-head">
          <div>
            <p className="guide-sheet-title">{exercise.name}</p>
            <p className="guide-sheet-meta">
              {t.guideEquipment}: {exercise.equipment}
            </p>
          </div>
          <button type="button" className="btn btn-dark" onClick={onClose}>{t.cancel}</button>
        </div>
        <figure className="guide-sheet-hero">
          <GuideLoop
            frames={frames}
            alt={`${exercise.name} ${labels[activeIndex]}`}
            activeIndex={activeIndex}
          />
          <figcaption>{labels[activeIndex]}</figcaption>
        </figure>
        {muscles.length > 0 && (
          <div className="guide-sheet-muscles">
            {muscles.map((key) => (
              <span key={key} className="guide-sheet-chip">{getMuscleLabel(key, language)}</span>
            ))}
          </div>
        )}
        <p className="guide-credit">{t.guideCredit}</p>
      </div>
    </div>
  );
}
